import cn from 'clsx'
import React from 'react'
import { DropTarget } from 'react-dnd'
import { BRIEF } from '@disco/constants/ItemTypes'
import { getMovingBriefs } from '@disco/state-management/reducers/entities/briefs'
import { connect } from 'react-redux'
import { SelectableItem } from '@disco/types/types/selection'
import { ExplorerPane } from '@disco/types/types/explorer'
import { Brief, BriefProjectId } from '@disco/types/types/brief'
import { getExplorerPanes } from '@disco/state-management/selectors/explorer'
import { moveBriefsToBriefProject } from '@disco/state-management/reducers/entities/briefs'
import { isDraggedItemPartOfSelection } from '@disco/utils/selection'
import {
  closePane,
  updatePanes,
} from '@disco/state-management/actions/ExplorerActions'
import { __REACT_NATIVE__ } from '@disco/constants/environment'

type Props = {
  isOver: boolean
  canDrop?: boolean
  connectDropTarget: (element: React.ReactElement) => any
  children?: React.ReactNode
  project: { briefProjectId: BriefProjectId; briefProjectName: string } | null
  draggedItem: { brief: Brief } | null | undefined
  dispatch: any
  selectedItems?: SelectableItem[]
  movingBriefs: number[]
  pane?: ExplorerPane
  panes: ExplorerPane[]
}

type BriefDropParams = {
  dispatch: any
  selectedItems?: SelectableItem[]
  pane?: ExplorerPane
  movingBriefs: number[]
  panes: ExplorerPane[]
  project: { briefProjectId: BriefProjectId; briefProjectName: string } | null
}

export const handleMoveBriefsToBriefProject = (
  draggedItem: { brief: Brief } | null | undefined,
  {
    dispatch,
    selectedItems,
    pane,
    movingBriefs,
    panes,
    project,
  }: BriefDropParams,
) => {
  if (!draggedItem.brief || !dispatch) {
    return
  }

  // Determine target project info
  let toProjectId: BriefProjectId | null
  let projectName: string | undefined
  const fromProjectId = draggedItem.brief.project?.id ?? null

  toProjectId = project?.briefProjectId ?? null
  projectName = project?.briefProjectName

  const multipleBriefsSelected =
    selectedItems?.length > 0 &&
    isDraggedItemPartOfSelection(selectedItems, draggedItem)

  let briefsToMove: Brief[] = []

  if (!multipleBriefsSelected) {
    const updatedBrief = {
      ...draggedItem.brief,
      project: toProjectId ? { id: toProjectId, name: projectName } : null,
    }
    briefsToMove.push(updatedBrief)
  } else {
    briefsToMove = selectedItems.map((item: Brief) => ({
      ...item,
      project: toProjectId ? { id: toProjectId, name: projectName } : null,
    }))
  }

  const briefIds = briefsToMove.map(brief => brief.id)
  const alreadyMoving = movingBriefs.some(briefId => briefIds.includes(briefId))

  if (alreadyMoving) {
    return
  }

  // Perform optimistic updates before calling the reducer
  const updatedPanes = [...panes] as ExplorerPane[]

  // check if we need to optimistic update target pane
  if (toProjectId === null || (toProjectId && !(pane.index === 0))) {
    const updatedTargetPane = {
      ...pane,
      results: [...briefsToMove, ...pane.results],
    }

    updatedPanes[pane.index] = updatedTargetPane
  }

  if (toProjectId && pane.index === 0) {
    const targetProjectPane = updatedPanes.find(
      pane => pane.meta?.briefProjectId === toProjectId,
    )
    if (targetProjectPane) {
      const updatedTargetPane = {
        ...targetProjectPane,
        results: [...briefsToMove, ...targetProjectPane.results],
      }

      updatedPanes[targetProjectPane.index] = updatedTargetPane
    }
  }

  const sourcePane = fromProjectId
    ? updatedPanes.find(pane => pane.meta?.briefProjectId === fromProjectId)
    : updatedPanes[0]

  if (sourcePane) {
    updatedPanes[sourcePane.index] = {
      ...sourcePane,
      results: sourcePane.results.filter(
        brief => !briefsToMove.some(movedBrief => movedBrief.id === brief.id),
      ),
    } as ExplorerPane
  }

  // Check if we need to close the brief details pane before updating
  const briefDetailsPane = updatedPanes[2]
  let shouldClosePane = false

  if (
    briefDetailsPane &&
    'meta' in briefDetailsPane &&
    briefDetailsPane.meta?.briefId
  ) {
    const openBriefId = briefDetailsPane.meta.briefId
    const isOpenBriefBeingMoved = briefsToMove.some(
      movedBrief => movedBrief.id === openBriefId,
    )

    if (isOpenBriefBeingMoved) {
      shouldClosePane = true

      // Update URL to reflect the new pane structure (remove briefId from URL)
      if (!__REACT_NATIVE__ && fromProjectId) {
        window.history.pushState({}, '', `/briefs/projects/${fromProjectId}`)
      }
    }
  }

  if (shouldClosePane) {
    dispatch(closePane(2))
  }
  const finalPanes = shouldClosePane ? updatedPanes.slice(0, 2) : updatedPanes

  dispatch(updatePanes(finalPanes))

  dispatch(
    moveBriefsToBriefProject({
      briefs: briefsToMove,
      toProjectId,
      fromProjectId,
      pane,
    }),
  )
}

const briefProjectTarget = {
  canDrop({ project }: any, monitor: any) {
    const item = monitor.getItem()
    const isShallowOver = monitor.isOver({ shallow: true })

    if (isShallowOver && item.brief) {
      return project?.briefProjectId
        ? item.brief.project?.id !== project.briefProjectId
        : // Dropping on root explorer pane, only allow dropping if the brief is currently in a project
          item.brief.project !== null
    }

    return false
  },

  drop(
    { dispatch, selectedItems, project, pane, movingBriefs, panes }: any,
    monitor: any,
  ) {
    // don't handle drop if child already handled it
    if (monitor.didDrop()) return

    const draggedItem = monitor.getItem()

    handleMoveBriefsToBriefProject(draggedItem, {
      dispatch,
      selectedItems,
      pane,
      movingBriefs,
      panes,
      project,
    })
  },
}

function collect(connect, monitor) {
  return {
    connectDropTarget: connect.dropTarget(),
    isOver: monitor.isOver({
      shallow: true,
    }),
    canDrop: monitor.canDrop(),
    draggedItem: monitor.getItem(),
  }
}

const BriefProjectDropWrapper = ({
  canDrop,
  children,
  connectDropTarget,
  isOver,
}: Props) => {
  const dropTargetClass = cn({
    HighlightedDropTarget: isOver && canDrop,
  })

  return connectDropTarget(<div className={dropTargetClass}>{children}</div>)
}

const DropTargetWrapper = DropTarget(
  [BRIEF],
  briefProjectTarget,
  collect,
)(BriefProjectDropWrapper)

export default connect(
  state => ({
    movingBriefs: getMovingBriefs(state),
    panes: getExplorerPanes(state),
  }),
  null,
)(DropTargetWrapper)
